import { BuyPlanPageAdditionalSeatsProps } from './BuyPlanPageAdditionalSeatsProps'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { PlanExtraComposite, PlanExtraDetail } from '@dtx-company/true-common/src/types/Plan'
import { PlanInterval } from '@dtx-company/true-common/src/types/PlanInterval'
import { getPriceText } from '@app/common/src/utils/getPriceText'
import { useBuyPlanPagePlanExtras } from './useBuyPlanPagePlanExtras'

/*  Checks the plan extras and returns the plan extra details (which is a subset of the planExtra that only 
  includes the planExtraId and quantity used to create a subscription) belonging to plan extras that have 
  a type of seats. This hook could potentially be modified to include other plan extras we add if they end up 
  needing the same logic/functionality.
*/
export function useBuyPlanPageAdditionalSeats({
  planType,
  currentSubscriptionInfoData,
  selectedPlan
}: BuyPlanPageAdditionalSeatsProps): {
  planExtra: PlanExtraComposite | undefined
  planExtraSeatsDetails: PlanExtraDetail | undefined
  setAdditionalSeatsQuantity: Dispatch<SetStateAction<number>>
  numBaseSeats: string
  pricePerSeat: string
} {
  const [planExtraSeatsDetails, setPlanExtraSeatsDetails] = useState<PlanExtraDetail>()
  const [numBaseSeats, setNumBaseSeats] = useState('')
  const [seatPlanExtra, setSeatPlanExtra] = useState<PlanExtraComposite | undefined>()
  const [pricePerSeat, setPricePerSeat] = useState('')
  const [additionalSeatsQuantity, setAdditionalSeatsQuantity] = useState(0)

  const planExtras = useBuyPlanPagePlanExtras({
    planType,
    currentSubscriptionInfoData,
    selectedPlan
  })

  // assign which plan extra is seatplanextra, updates when user selects
  // different plan with different plan extras
  useEffect(() => {
    if (!planExtras) return
    planExtras.forEach(planExtra => {
      if (planExtra.planExtraType === 'seats') {
        setSeatPlanExtra(planExtra)
        setNumBaseSeats(planExtra.minQuantity.toString())
      }
    })
  }, [planExtras])

  // set price when user changes plan interval or plan extra
  useEffect(() => {
    if (!seatPlanExtra) return
    setPricePerSeat(
      getPriceText({
        priceInCents:
          selectedPlan?.interval === PlanInterval.YEARLY
            ? seatPlanExtra.unitPriceInCents / 12
            : seatPlanExtra.unitPriceInCents
      })
    )
  }, [selectedPlan?.interval, seatPlanExtra])

  // assign quantity to the plan extra details when quantity is updated
  useEffect(() => {
    if (!seatPlanExtra) return
    setPlanExtraSeatsDetails({
      planExtraId: seatPlanExtra.planExtraId,
      quantity: additionalSeatsQuantity ? additionalSeatsQuantity : seatPlanExtra.currentQuantity
    })
  }, [additionalSeatsQuantity, seatPlanExtra])

  // enforce quantity bounds when user changes plan interval or quantity
  useEffect(() => {
    if (!seatPlanExtra?.minQuantity || !seatPlanExtra?.maxQuantity) return
    if (additionalSeatsQuantity < seatPlanExtra?.minQuantity)
      setAdditionalSeatsQuantity(seatPlanExtra?.minQuantity)
    else if (additionalSeatsQuantity > seatPlanExtra?.maxQuantity)
      setAdditionalSeatsQuantity(seatPlanExtra?.maxQuantity)
  }, [additionalSeatsQuantity, seatPlanExtra?.maxQuantity, seatPlanExtra?.minQuantity])

  return {
    planExtra: seatPlanExtra,
    planExtraSeatsDetails,
    setAdditionalSeatsQuantity,
    numBaseSeats,
    pricePerSeat
  }
}
